/**
 * 省份大小配置
 * 根据实际地理面积对省份进行分类，提供不同的拼图块大小和容错范围
 */

export interface ProvinceSizeConfig {
  provinces: string[];
  blockSize: string;
  scale: number;
  tolerance: number;
  description: string;
}

export const PROVINCE_SIZE_CATEGORIES: Record<string, ProvinceSizeConfig> = {
  // 超大省份 (面积 > 100万km²)
  'extra-large': {
    provinces: [
      '新疆维吾尔自治区',
      '西藏自治区', 
      '内蒙古自治区'
    ],
    blockSize: 'w-28 h-28', // 112px × 112px
    scale: 1.4,
    tolerance: 3.5,
    description: '超大省份 - 面积超过100万平方公里'
  },
  
  // 大省份 (面积 50-100万km²)
  'large': {
    provinces: [
      '青海省',
      '四川省', 
      '黑龙江省',
      '甘肃省',
      '云南省',
      '广西壮族自治区',
      '湖南省',
      '陕西省',
      '河北省',
      '吉林省',
      '湖北省'
    ],
    blockSize: 'w-24 h-24', // 96px × 96px
    scale: 1.2,
    tolerance: 2.8,
    description: '大省份 - 面积50-100万平方公里'
  },
  
  // 中等省份 (面积 10-50万km²)
  'medium': {
    provinces: [
      '河南省',
      '山东省',
      '山西省',
      '辽宁省',
      '安徽省',
      '福建省',
      '江西省',
      '浙江省',
      '江苏省',
      '重庆市',
      '贵州省',
      '宁夏回族自治区'
    ],
    blockSize: 'w-20 h-20', // 80px × 80px (默认)
    scale: 1.0,
    tolerance: 2.0,
    description: '中等省份 - 面积10-50万平方公里'
  },
  
  // 小省份 (面积 < 10万km²)
  'small': {
    provinces: [
      '广东省',
      '海南省',
      '台湾省',
      '北京市',
      '天津市',
      '上海市'
    ],
    blockSize: 'w-16 h-16', // 64px × 64px
    scale: 0.8,
    tolerance: 1.2,
    description: '小省份 - 面积小于10万平方公里'
  },
  
  // 特别行政区
  'tiny': {
    provinces: [
      '香港特别行政区',
      '澳门特别行政区'
    ],
    blockSize: 'w-12 h-12', // 48px × 48px
    scale: 0.6,
    tolerance: 0.8,
    description: '特别行政区 - 面积极小'
  }
};

/**
 * 获取省份的大小分类
 */
export function getProvinceSizeCategory(provinceName: string): string {
  for (const [category, config] of Object.entries(PROVINCE_SIZE_CATEGORIES)) {
    if (config.provinces.includes(provinceName)) {
      return category;
    }
  }
  return 'medium'; // 默认中等大小
}

/**
 * 获取省份拼图块的大小配置
 */
export function getProvinceBlockSize(provinceName: string): {
  className: string;
  scale: number;
  category: string;
} {
  const category = getProvinceSizeCategory(provinceName);
  const config = PROVINCE_SIZE_CATEGORIES[category];
  return {
    className: config.blockSize,
    scale: config.scale,
    category
  };
}

/**
 * 获取省份的容错范围
 */
export function getProvinceTolerance(provinceName: string): number {
  const category = getProvinceSizeCategory(provinceName);
  return PROVINCE_SIZE_CATEGORIES[category].tolerance;
}

/**
 * 获取省份大小的描述信息
 */
export function getProvinceSizeDescription(provinceName: string): string {
  const category = getProvinceSizeCategory(provinceName);
  return PROVINCE_SIZE_CATEGORIES[category].description;
}

/**
 * 获取所有省份的大小统计
 */
export function getProvinceSizeStats(): Record<string, number> {
  const stats: Record<string, number> = {};
  
  Object.entries(PROVINCE_SIZE_CATEGORIES).forEach(([category, config]) => {
    stats[category] = config.provinces.length;
  });
  
  return stats;
}

/**
 * 验证省份名称是否在配置中
 */
export function isProvinceConfigured(provinceName: string): boolean {
  return getProvinceSizeCategory(provinceName) !== 'medium' || 
         PROVINCE_SIZE_CATEGORIES.medium.provinces.includes(provinceName);
}

/**
 * 获取拼图块的实际像素大小
 */
export function getBlockPixelSize(provinceName: string): { width: number; height: number } {
  const { scale } = getProvinceBlockSize(provinceName);
  const baseSize = 80; // w-20 h-20 = 80px
  const actualSize = baseSize * scale;
  
  return {
    width: actualSize,
    height: actualSize
  };
}

/**
 * 根据省份大小获取推荐的网格布局
 */
export function getRecommendedGridLayout(provinces: string[]): {
  columns: number;
  gap: string;
  containerClass: string;
} {
  const sizeStats = provinces.reduce((acc, provinceName) => {
    const category = getProvinceSizeCategory(provinceName);
    acc[category] = (acc[category] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  
  const hasLargeProvinces = (sizeStats['extra-large'] || 0) + (sizeStats['large'] || 0) > 0;
  const hasTinyProvinces = (sizeStats['tiny'] || 0) > 0;
  
  if (hasLargeProvinces && hasTinyProvinces) {
    // 混合大小，使用灵活布局
    return {
      columns: 4,
      gap: 'gap-3',
      containerClass: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4'
    };
  } else if (hasLargeProvinces) {
    // 主要是大省份
    return {
      columns: 3,
      gap: 'gap-4',
      containerClass: 'grid-cols-2 sm:grid-cols-3'
    };
  } else if (hasTinyProvinces) {
    // 主要是小省份
    return {
      columns: 6,
      gap: 'gap-2',
      containerClass: 'grid-cols-4 sm:grid-cols-5 lg:grid-cols-6'
    };
  } else {
    // 标准布局
    return {
      columns: 4,
      gap: 'gap-3',
      containerClass: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4'
    };
  }
}
